import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Batch 68: Pricing v4 — un'unica scala per business e agency
const PLAN_LABEL = {
  trial: "Trial 14gg",
  start: "Start",
  pro: "Pro",
  scale: "Scale",
  enterprise: "Oltre Scale (Enterprise)",
};

// Stessa scala per entrambi i tipi: l'add-on workspace è separato per agency
const PLANS_BY_TYPE = {
  business: ["trial", "start", "pro", "scale", "enterprise"],
  agency: ["trial", "start", "pro", "scale", "enterprise"],
};

const TYPE_LABEL = { business: "Azienda", agency: "Agenzia", personal: "Personal (legacy)" };

export default function AdminUserDialog({ userId, onClose, onChanged }) {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [planCode, setPlanCode] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delEmail, setDelEmail] = useState("");
  const [delCheck, setDelCheck] = useState(false);
  // Batch 66: Hard-delete (irreversibile) + reset password admin + tipo account toggle
  const [showHardDelete, setShowHardDelete] = useState(false);
  const [hdEmail, setHdEmail] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [showDemote, setShowDemote] = useState(false);
  const [demoteForce, setDemoteForce] = useState(false);
  const [demoteOrphans, setDemoteOrphans] = useState(0);

  const load = async () => {
    try {
      const { data: u } = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setData(u);
      setPlanCode(u.plan_code || "trial");
      setBillingCycle(u.billing_cycle || "monthly");
      const { data: log } = await axios.get(`${API}/admin/audit-log?user_id=${userId}&limit=20`, { withCredentials: true });
      setAudit(log.items || []);
    } catch { toast.error("Errore caricamento dettaglio"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const handleChangePlan = async () => {
    if (!planCode) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`,
        { plan_code: planCode, billing_cycle: billingCycle }, { withCredentials: true });
      toast.success(`Piano: ${PLAN_LABEL[planCode]} (${billingCycle === "yearly" ? "Annuale" : "Mensile"})`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleSuspend = async () => {
    if (!reason.trim()) { toast.error("Inserisci motivazione"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/suspend`, { reason }, { withCredentials: true });
      toast.success("Utente sospeso");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/reactivate`, {}, { withCredentials: true });
      toast.success("Utente riattivato");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleImpersonate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/impersonate`, {}, { withCredentials: true });
      toast.success("Impersonificazione attiva (15 min)");
      await refreshUser();
      onClose?.();
      navigate("/candidates");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (delEmail !== data.email) { toast.error("Email non corrisponde"); return; }
    if (!delCheck) { toast.error("Conferma il checkbox"); return; }
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("Account spostato in cestino (recuperabile 30gg)");
      setShowDelete(false);
      onChanged?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // Batch 66 — Hard Delete (irreversibile, libera la mail)
  const handleHardDelete = async () => {
    if (hdEmail !== data.email) { toast.error("Email non corrisponde"); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/destroy-user-by-email`,
        { email: data.email }, { withCredentials: true });
      toast.success(`Utente ${data.email} eliminato definitivamente. Mail disponibile per nuova registrazione.`);
      setShowHardDelete(false);
      onChanged?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // Batch 66 — Send password reset email (admin-initiated, valid 24h)
  const handleSendResetPw = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/users/${userId}/send-password-reset`,
        {}, { withCredentials: true });
      if (r.email_sent) toast.success(`Link reset password inviato a ${r.email}`);
      else toast.warning(`Token generato ma email non recapitata (${r.email}). Verifica configurazione Resend.`);
      setShowResetPw(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // Batch 66 — Tipo account toggle: Business (single-ws) ↔ Agency (multi-ws)
  const handleToggleAccountType = async (newType) => {
    if (!data) return;
    if (newType === data.account_type) return;
    // Trova primo workspace owner per chiamare promote/demote-to-agency
    const ws = (data.workspaces || []).find((w) => !w.deleted_at);
    if (!ws) { toast.error("Nessun workspace attivo per questo utente"); return; }
    if (newType === "agency") {
      setBusy(true);
      try {
        const { data: r } = await axios.post(`${API}/admin/workspaces/${ws.id}/promote-to-agency`,
          {}, { withCredentials: true });
        if (r.already) toast.info(`${r.email || data.email} è già Agenzia`);
        else toast.success(`${r.email || data.email} promosso ad Agenzia`);
        setPlanCode("start");
        await load(); onChanged?.();
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      finally { setBusy(false); }
    } else if (newType === "business") {
      // Pre-check active client workspaces
      const activeClients = (data.workspaces || []).filter((w) => w.type === "client" && !w.deleted_at).length;
      setDemoteOrphans(activeClients);
      setDemoteForce(false);
      setShowDemote(true);
    }
  };

  const handleConfirmDemote = async () => {
    const ws = (data.workspaces || []).find((w) => !w.deleted_at);
    if (!ws) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/workspaces/${ws.id}/demote-to-business`,
        { force: demoteForce }, { withCredentials: true });
      toast.success(`${r.email || data.email} declassato ad Azienda${r.orphaned_clients ? ` (${r.orphaned_clients} client ws orfani)` : ""}`);
      setShowDemote(false);
      setPlanCode("start");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // ── Bonus Analyses (Analisi Omaggio) ─────────────
  const [bonus, setBonus] = useState({ balance: 0, grants: [] });
  const [showGrant, setShowGrant] = useState(false);
  const [grantAmount, setGrantAmount] = useState(20);
  const [grantReason, setGrantReason] = useState("");
  const [grantExpires, setGrantExpires] = useState("");
  const [revokeFor, setRevokeFor] = useState(null); // grant_id
  const [revokeReason, setRevokeReason] = useState("");

  const loadBonus = async () => {
    try {
      const { data: b } = await axios.get(`${API}/admin/users/${userId}/bonus-analyses`, { withCredentials: true });
      setBonus(b);
    } catch { /* silent */ }
  };
  useEffect(() => { if (userId) loadBonus(); /* eslint-disable-next-line */ }, [userId]);

  const handleGrant = async () => {
    if (!grantReason.trim()) { toast.error("Inserisci motivo"); return; }
    if (!grantAmount || grantAmount < 1) { toast.error("Quantita non valida"); return; }
    setBusy(true);
    try {
      const payload = { amount: Number(grantAmount), reason: grantReason };
      if (grantExpires) payload.expires_at = new Date(grantExpires + "T23:59:59Z").toISOString();
      await axios.post(`${API}/admin/users/${userId}/bonus-analyses`, payload, { withCredentials: true });
      toast.success(`${grantAmount} analisi omaggio assegnate`);
      setShowGrant(false); setGrantReason(""); setGrantAmount(20); setGrantExpires("");
      await loadBonus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleRevoke = async () => {
    if (!revokeReason.trim()) { toast.error("Inserisci motivo revoca"); return; }
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}/bonus-analyses/${revokeFor}`,
        { data: { revoke_reason: revokeReason }, withCredentials: true });
      toast.success("Grant revocato");
      setRevokeFor(null); setRevokeReason("");
      await loadBonus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  if (!data) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Caricamento utente</DialogTitle></DialogHeader>
        <div className="py-8 text-center text-[#0D1F3C]/40">Caricamento…</div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-user-dialog">
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-3">
              <span>{data.email}</span>
              {data.is_admin && <Badge className="bg-red-600 text-white">admin</Badge>}
              {data.is_suspended && <Badge className="bg-orange-100 text-orange-700">Sospeso</Badge>}
              {data.is_deleted && <Badge className="bg-red-100 text-red-700">Eliminato</Badge>}
            </span>
          </DialogTitle>
          <p className="text-xs text-[#0D1F3C]/60">{data.first_name} {data.last_name} — {data.company_name || "—"}</p>
        </DialogHeader>

        <Tabs defaultValue="piano">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="piano" data-testid="tab-piano">Piano</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
            <TabsTrigger value="workspaces" data-testid="tab-workspaces">Workspaces</TabsTrigger>
            <TabsTrigger value="analisi" data-testid="tab-analisi">Analisi</TabsTrigger>
            <TabsTrigger value="azioni" data-testid="tab-azioni">Azioni</TabsTrigger>
            <TabsTrigger value="storia" data-testid="tab-storia">Storia</TabsTrigger>
          </TabsList>

          <TabsContent value="piano" className="space-y-3 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-[#0D1F3C]/80" data-testid="piano-info-banner">
              <strong>Tipo account</strong> = struttura tecnica (Azienda single-workspace o Agenzia multi-cliente). <strong>Piano</strong> = tier di pagamento. Sono indipendenti: cambiare il piano da solo NON converte un'Azienda in Agenzia.
            </div>
            <div className="bg-[#F4F7FB] rounded-lg p-4 space-y-2 text-sm">
              <div><strong>Piano:</strong> {PLAN_LABEL[data.plan_code] || data.plan_code} · <strong>Cadenza:</strong> {data.billing_cycle === "yearly" ? "Annuale" : data.billing_cycle === "monthly" ? "Mensile" : "—"}</div>
              <div><strong>Account type:</strong> {TYPE_LABEL[data.account_type] || data.account_type}</div>
              <div><strong>Analisi e generazioni usate:</strong> {data.tests_used_this_month} / {data.max_tests_per_month}</div>
              {data.trial_ends_at && <div><strong>Trial:</strong> fino al {new Date(data.trial_ends_at).toLocaleDateString("it-IT")}</div>}
              <div><strong>Pagato:</strong> € {(data.revenue_paid_total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
              <div><strong>Prossimo:</strong> € {(data.next_billing_amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })} {data.next_billing_date && `(${new Date(data.next_billing_date).toLocaleDateString("it-IT")})`}</div>
            </div>

            {/* Tipo account toggle */}
            <div className="border border-[#0D1F3C]/10 rounded-lg p-3">
              <label className="text-xs font-bold uppercase tracking-wider text-[#0D1F3C]/60 mb-2 block">Tipo account</label>
              <div className="flex gap-2" role="radiogroup" data-testid="account-type-toggle">
                <Button type="button" size="sm" variant={data.account_type === "business" ? "default" : "outline"}
                  onClick={() => handleToggleAccountType("business")} disabled={busy || data.account_type === "business"}
                  className={data.account_type === "business" ? "bg-[#0D1F3C] text-white" : ""}
                  data-testid="account-type-business-btn">
                  Azienda (single workspace)
                </Button>
                <Button type="button" size="sm" variant={data.account_type === "agency" ? "default" : "outline"}
                  onClick={() => handleToggleAccountType("agency")} disabled={busy || data.account_type === "agency"}
                  className={data.account_type === "agency" ? "bg-[#1B4FD8] text-white" : ""}
                  data-testid="account-type-agency-btn">
                  Agenzia (multi-cliente)
                </Button>
              </div>
              <p className="text-xs text-[#0D1F3C]/50 mt-2">
                {data.account_type === "agency"
                  ? "Account Agenzia: può creare workspace clienti dalla sidebar."
                  : "Account Azienda: workspace singolo per la propria organizzazione."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Piano (filtrato per tipo)</label>
                <Select value={planCode} onValueChange={setPlanCode}>
                  <SelectTrigger data-testid="admin-user-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(PLANS_BY_TYPE[data.account_type] || Object.keys(PLAN_LABEL)).map((code) => (
                      <SelectItem key={code} value={code}>{PLAN_LABEL[code] || code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Cadenza</label>
                <Select value={billingCycle} onValueChange={setBillingCycle}>
                  <SelectTrigger data-testid="admin-user-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensile</SelectItem>
                    <SelectItem value="yearly">Annuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleChangePlan} disabled={busy} data-testid="admin-user-change-plan-btn" className="w-full">
              Applica modifica piano
            </Button>
          </TabsContent>

          <TabsContent value="account" className="space-y-2 pt-4 text-sm">
            <div className="bg-[#F4F7FB] rounded-lg p-4 space-y-1">
              <div><strong>ID:</strong> <span className="font-mono text-xs">{data.id}</span></div>
              <div><strong>Email:</strong> {data.email}</div>
              <div><strong>Auth:</strong> {data.auth_provider || "password"}</div>
              <div><strong>Region:</strong> {data.region || "—"}</div>
              <div><strong>Workspace:</strong> {data.workspace_count} / {data.max_workspaces_allowed}</div>
              <div><strong>Creato:</strong> {data.created_at ? new Date(data.created_at).toLocaleString("it-IT") : "—"}</div>
              <div><strong>Ultimo login:</strong> {data.last_login_at ? new Date(data.last_login_at).toLocaleString("it-IT") : "—"}</div>
              {data.is_deleted && <div className="text-red-700"><strong>Eliminato:</strong> {new Date(data.deleted_at).toLocaleString("it-IT")}</div>}
            </div>
          </TabsContent>

          <TabsContent value="workspaces" className="pt-4">
            {(data.workspaces || []).length === 0 ? (
              <p className="text-sm text-[#0D1F3C]/50 text-center py-6">Nessun workspace</p>
            ) : (
              <div className="space-y-2">
                {data.workspaces.map((w) => (
                  <div key={w.id} className="bg-white border border-[#0D1F3C]/8 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{w.name || "—"}</div>
                      <div className="text-xs text-[#0D1F3C]/60">{w.id.slice(0, 12)}… · {w.type} · {w.deleted_at ? "DELETED" : "active"}</div>
                    </div>
                    <Badge variant="outline">{w.type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analisi" className="pt-4 space-y-3" data-testid="bonus-analyses-tab">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Analisi Omaggio disponibili</div>
                <div className="text-3xl font-bold text-emerald-700" data-testid="bonus-balance">{bonus.balance}</div>
                {bonus.expires_next_at && (
                  <div className="text-xs text-emerald-700/70 mt-1">
                    Prossima scadenza: {new Date(bonus.expires_next_at).toLocaleDateString("it-IT")}
                  </div>
                )}
              </div>
              <Button onClick={() => setShowGrant(true)} disabled={busy || data.is_deleted}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="grant-bonus-btn">
                Regala analisi
              </Button>
            </div>
            <div>
              <h4 className="text-xs font-bold text-[#0D1F3C]/60 uppercase tracking-wider mb-2">Storico Grant</h4>
              {(bonus.grants || []).length === 0 ? (
                <p className="text-sm text-[#0D1F3C]/50 text-center py-6">Nessun grant emesso</p>
              ) : (
                <div className="space-y-2">
                  {bonus.grants.map((g) => (
                    <div key={g.grant_id} className="bg-white border border-[#0D1F3C]/8 rounded-lg p-3 text-xs"
                         data-testid={`grant-row-${g.grant_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className={
                            g.status === "active" ? "bg-emerald-100 text-emerald-700" :
                            g.status === "consumed" ? "bg-blue-100 text-blue-700" :
                            g.status === "revoked" ? "bg-red-100 text-red-700" :
                            "bg-gray-100 text-gray-700"
                          }>{g.status}</Badge>
                          <span className="font-mono">{g.amount} (residuo {g.residual})</span>
                        </div>
                        <span className="text-[#0D1F3C]/50">{new Date(g.granted_at).toLocaleString("it-IT")}</span>
                      </div>
                      <div className="text-[#0D1F3C]/70">
                        Admin: {g.admin_email} · Motivo: {g.reason}
                        {g.expires_at && <> · Scade: {new Date(g.expires_at).toLocaleDateString("it-IT")}</>}
                      </div>
                      {g.revoked_at && (
                        <div className="text-red-700 mt-1">
                          Revocato il {new Date(g.revoked_at).toLocaleString("it-IT")} da {g.revoked_by_email} — Motivo: {g.revoke_reason}
                        </div>
                      )}
                      {g.status === "active" && (
                        <Button size="sm" variant="outline" className="mt-2 border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => { setRevokeFor(g.grant_id); setRevokeReason(""); }}
                          data-testid={`revoke-grant-${g.grant_id}`}>
                          Revoca
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="azioni" className="space-y-3 pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              ⚠️ Le azioni sono auditate. Non puoi modificare il tuo stesso account.
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Motivazione (sospensione)</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Inserisci motivo..." data-testid="admin-user-suspend-reason" />
              </div>
              <div className="flex flex-wrap gap-2">
                {!data.is_suspended ? (
                  <Button variant="destructive" onClick={handleSuspend} disabled={busy || data.is_deleted} data-testid="admin-user-suspend-btn">
                    Sospendi
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleReactivate} disabled={busy} data-testid="admin-user-reactivate-btn">
                    Riattiva
                  </Button>
                )}
                <Button variant="outline" onClick={handleImpersonate} disabled={busy || data.is_suspended || data.is_admin || data.is_deleted}
                  className="border-orange-500 text-orange-700 hover:bg-orange-50" data-testid="admin-user-impersonate-btn">
                  Impersonifica (15 min)
                </Button>
              </div>
              <div className="border-t border-[#0D1F3C]/8 pt-3 mt-3 space-y-3">
                <div>
                  <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Reset password</h4>
                  <Button variant="outline" onClick={() => setShowResetPw(true)}
                    disabled={busy || data.is_deleted}
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    data-testid="admin-user-send-reset-pw-btn">
                    Invia link reset password
                  </Button>
                </div>

                <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2 pt-2 border-t border-[#0D1F3C]/8">Zona pericolosa</h4>
                {data.is_deleted ? (
                  <div className="space-y-2">
                    <p className="text-xs text-[#0D1F3C]/60">Spostato in cestino il {new Date(data.deleted_at).toLocaleString("it-IT")}. Hard-delete automatico entro 30 giorni.</p>
                    <Button variant="destructive" onClick={() => { setShowHardDelete(true); setHdEmail(""); }}
                      disabled={busy || data.is_admin}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="admin-user-hard-delete-btn">
                      Elimina definitivamente
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => setShowDelete(true)}
                      disabled={busy || data.is_admin}
                      className="border-orange-500 text-orange-700 hover:bg-orange-50"
                      data-testid="admin-user-delete-btn">
                      Sposta in cestino (30gg)
                    </Button>
                    <Button variant="destructive" onClick={() => { setShowHardDelete(true); setHdEmail(""); }}
                      disabled={busy || data.is_admin}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="admin-user-hard-delete-btn">
                      Elimina definitivamente
                    </Button>
                  </div>
                )}
              </div>
              {data.is_suspended && (
                <div className="text-xs text-red-700 bg-red-50 p-3 rounded-lg">
                  <strong>Sospeso:</strong> {data.suspension_reason || "—"}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="storia" className="pt-4 space-y-2">
            {audit.length === 0 ? (
              <p className="text-sm text-[#0D1F3C]/50 text-center py-6">Nessuna azione registrata</p>
            ) : (
              audit.map((a, i) => (
                <div key={i} className="bg-[#F4F7FB] rounded-lg p-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline">{a.action}</Badge>
                    <span className="text-[#0D1F3C]/50">{new Date(a.at).toLocaleString("it-IT")}</span>
                  </div>
                  <div className="text-[#0D1F3C]/70">
                    Admin: {a.admin_email}
                    {a.payload?.from && a.payload?.to && <> · {a.payload.from} → <strong>{a.payload.to}</strong></>}
                    {a.payload?.reason && <> · {a.payload.reason}</>}
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    <Dialog open={showDelete} onOpenChange={setShowDelete}>
      <DialogContent data-testid="delete-confirm-dialog">
        <DialogHeader><DialogTitle>Sposta in cestino — Conferma</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-[#0D1F3C]/80">Stai per spostare in cestino <strong>{data.email}</strong>. Recuperabile per 30 giorni, poi cancellato definitivamente.</p>
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Digita l&apos;email per confermare</label>
            <Input value={delEmail} onChange={(e) => setDelEmail(e.target.value)} placeholder={data.email} data-testid="delete-confirm-email" />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="hd-confirm" checked={delCheck} onCheckedChange={setDelCheck} data-testid="delete-confirm-check" />
            <label htmlFor="hd-confirm" className="text-xs text-[#0D1F3C]/80 cursor-pointer">
              Confermo: hard-delete automatico dopo 30 giorni (recuperabile fino ad allora)
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDelete(false)} disabled={busy}>Annulla</Button>
            <Button variant="outline" onClick={handleDelete} disabled={busy || delEmail !== data.email || !delCheck}
              className="border-orange-500 text-orange-700 hover:bg-orange-50"
              data-testid="delete-confirm-btn">
              Sposta in cestino
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Batch 66 — Hard Delete (irreversibile, libera la mail) */}
    <Dialog open={showHardDelete} onOpenChange={setShowHardDelete}>
      <DialogContent data-testid="hard-delete-dialog">
        <DialogHeader><DialogTitle className="text-red-700">Elimina definitivamente — IRREVERSIBILE</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border-2 border-red-300 rounded-md p-3 text-xs text-red-900">
            <strong>⚠ Questa azione è IRREVERSIBILE.</strong> Tutti i workspace, dipendenti, candidati, analisi, proposte e fatture associate a <strong>{data.email}</strong> saranno cancellati permanentemente. La mail tornerà disponibile per nuova registrazione.
          </div>
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">
              Digita l&apos;email <strong>{data.email}</strong> per confermare
            </label>
            <Input value={hdEmail} onChange={(e) => setHdEmail(e.target.value)} placeholder={data.email}
              data-testid="hard-delete-confirm-email" autoComplete="off" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowHardDelete(false)} disabled={busy}>Annulla</Button>
            <Button onClick={handleHardDelete} disabled={busy || hdEmail !== data.email}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="hard-delete-confirm-btn">
              Elimina definitivamente
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Batch 66 — Send password reset (admin → user, valid 24h) */}
    <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
      <DialogContent data-testid="send-reset-pw-dialog">
        <DialogHeader><DialogTitle>Invia link reset password</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-[#0D1F3C]/80">
            Verrà inviata una email a <strong>{data.email}</strong> con un link per impostare una nuova password (validità 24h).
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-xs text-[#0D1F3C]/70">
            L&apos;utente potrà scegliere una nuova password autonomamente. L&apos;azione viene tracciata in audit log.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowResetPw(false)} disabled={busy}>Annulla</Button>
            <Button onClick={handleSendResetPw} disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="send-reset-pw-confirm-btn">
              Invia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Batch 66 — Demote agency → business with active-clients guard */}
    <Dialog open={showDemote} onOpenChange={setShowDemote}>
      <DialogContent data-testid="demote-confirm-dialog">
        <DialogHeader><DialogTitle>Declassa ad Azienda — Conferma</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {demoteOrphans > 0 ? (
            <>
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-xs text-amber-900">
                <strong>⚠ Attenzione:</strong> Questo account ha <strong>{demoteOrphans} workspace clienti attivi</strong>. Declassarli a Azienda li renderà orfani (l&apos;owner non potrà più accedervi dalla sidebar multi-workspace).
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="demote-force" checked={demoteForce} onCheckedChange={setDemoteForce} data-testid="demote-force-check" />
                <label htmlFor="demote-force" className="text-xs text-[#0D1F3C]/80 cursor-pointer">
                  Confermo: declassa comunque, accetto l&apos;orfanizzazione dei {demoteOrphans} workspace clienti
                </label>
              </div>
            </>
          ) : (
            <p className="text-[#0D1F3C]/80">Nessun workspace cliente attivo. Procedere col declassamento ad Azienda single-workspace?</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDemote(false)} disabled={busy}>Annulla</Button>
            <Button variant="destructive" onClick={handleConfirmDemote}
              disabled={busy || (demoteOrphans > 0 && !demoteForce)}
              data-testid="demote-confirm-btn">
              Declassa ad Azienda
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dialog: Regala analisi omaggio */}
    <Dialog open={showGrant} onOpenChange={setShowGrant}>
      <DialogContent data-testid="grant-bonus-dialog">
        <DialogHeader><DialogTitle>Regala analisi omaggio</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Quantita</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[5, 10, 20, 50, 100, 200].map((n) => (
                <Button key={n} type="button" size="sm"
                  variant={grantAmount === n ? "default" : "outline"}
                  onClick={() => setGrantAmount(n)}
                  data-testid={`grant-preset-${n}`}>
                  {n}
                </Button>
              ))}
            </div>
            <Input type="number" min="1" max="1000" value={grantAmount}
              onChange={(e) => setGrantAmount(Number(e.target.value))}
              data-testid="grant-amount-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Motivo (obbligatorio)</label>
            <Input value={grantReason} onChange={(e) => setGrantReason(e.target.value)}
              placeholder="es. Compensazione disservizio del 12/04, demo enterprise, estensione trial..."
              data-testid="grant-reason-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Scadenza (opzionale)</label>
            <Input type="date" value={grantExpires} onChange={(e) => setGrantExpires(e.target.value)}
              data-testid="grant-expires-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowGrant(false)} disabled={busy}>Annulla</Button>
            <Button onClick={handleGrant} disabled={busy || !grantReason.trim() || grantAmount < 1}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="grant-confirm-btn">
              Regala {grantAmount} analisi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dialog: Revoca grant */}
    <Dialog open={!!revokeFor} onOpenChange={(o) => !o && setRevokeFor(null)}>
      <DialogContent data-testid="revoke-bonus-dialog">
        <DialogHeader><DialogTitle>Revoca grant</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-[#0D1F3C]/80">La revoca azzera il residuo del grant. Operazione tracciata in audit log.</p>
          <div>
            <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Motivo revoca (obbligatorio)</label>
            <Input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)}
              data-testid="revoke-reason-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRevokeFor(null)} disabled={busy}>Annulla</Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={busy || !revokeReason.trim()}
              data-testid="revoke-confirm-btn">
              Revoca
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
